class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.dialog = this.querySelector('dialog');
    this.status = this.querySelector('[data-cart-status]');
    this.error = this.querySelector('[data-cart-error]');
    this.busy = false;
    window.muzeCart = this;

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin || url.pathname.replace(/\/$/, '') !== `${this.root}cart`.replace(/\/$/, '') || url.search || link.closest('cart-drawer')) return;
      event.preventDefault();
      this.open(link);
      this.refresh();
    }, options);

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (form.closest('cart-drawer') === this) {
        const invalidQuantity = [...this.querySelectorAll('[data-cart-quantity]')].find((input) => !input.checkValidity());
        if (this.busy || invalidQuantity) {
          event.preventDefault();
          invalidQuantity?.reportValidity();
        }
        return;
      }
      if (!form.matches('form.product-form') || event.defaultPrevented) return;
      if (event.submitter && !event.submitter.matches('[data-add-to-cart], [data-sticky-add-to-cart]')) return;
      event.preventDefault();
      if (this.busy) return;
      const trigger = event.submitter || form.querySelector('[data-add-to-cart]');
      const data = new FormData(form);
      const buttons = [...document.querySelectorAll('[data-add-to-cart], [data-sticky-add-to-cart]')].filter((button) => button.form === form);
      const states = buttons.map((button) => button.disabled);
      buttons.forEach((button) => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
      this.add(data, trigger).finally(() => {
        buttons.forEach((button, index) => {
          if (new FormData(form).get('id') === data.get('id')) button.disabled = states[index];
          button.removeAttribute('aria-busy');
        });
        if (!this.dialog.open && trigger?.isConnected) trigger.focus({ preventScroll: true });
      });
    }, options);

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-close]')) this.close();
      if (event.target.closest('[data-cart-retry]')) this.refresh();
      const remove = event.target.closest('[data-cart-remove]');
      if (remove) this.change(remove.closest('[data-cart-item]').dataset.cartKey, 0);
      if (event.target === this.dialog) {
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      }
    }, options);
    this.addEventListener('change', (event) => {
      const input = event.target.closest('[data-cart-quantity]');
      if (!input || !input.reportValidity()) return;
      this.change(input.closest('[data-cart-item]').dataset.cartKey, Number(input.value));
    }, options);
    this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, options);
    this.dialog.addEventListener('close', () => {
      this.setExpanded(false);
      if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
    }, options);
    window.addEventListener('pageshow', () => { if (this.dialog.open) this.refresh(); }, options);
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.closeTimer);
    if (window.muzeCart === this) delete window.muzeCart;
  }

  get root() { return window.Shopify?.routes?.root || '/'; }

  setExpanded(open) {
    document.querySelectorAll('.site-header__cart').forEach((link) => {
      link.setAttribute('aria-haspopup', 'dialog');
      link.setAttribute('aria-controls', 'CartDrawer');
      link.setAttribute('aria-expanded', String(open));
    });
  }

  open(trigger) {
    clearTimeout(this.closeTimer);
    this.dialog.classList.remove('is-closing');
    if (!this.dialog.open) {
      this.returnFocus = trigger || document.activeElement;
      this.dialog.showModal();
    }
    this.setExpanded(true);
  }

  close() {
    if (!this.dialog.open) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) { this.dialog.close(); return; }
    this.dialog.classList.add('is-closing');
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => { this.dialog.close(); this.dialog.classList.remove('is-closing'); }, 180);
  }

  setBusy(busy) {
    this.busy = busy;
    this.setAttribute('aria-busy', String(busy));
    this.querySelectorAll('[data-cart-content] button, [data-cart-content] input, [data-cart-retry]').forEach((control) => { control.disabled = busy; });
  }

  message(text, added = false) {
    this.status.classList.toggle('is-added', added);
    this.status.textContent = text;
  }

  render(html) {
    if (!html) throw new Error(this.dataset.refreshErrorLabel);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const content = doc.querySelector('[data-cart-content]');
    if (!content) throw new Error(this.dataset.refreshErrorLabel);
    const current = this.querySelector('[data-cart-content]');
    const focused = Boolean(this.focusKey);
    const focusedKey = this.focusKey;
    const wasQuantity = this.focusQuantity;
    const scrollTop = current.scrollTop;
    current.replaceWith(content);
    content.scrollTop = scrollTop;
    this.setBusy(this.busy);
    if (focused && this.dialog.open) {
      const row = [...content.querySelectorAll('[data-cart-item]')].find((item) => item.dataset.cartKey === focusedKey);
      // Restore focus after controls are re-enabled by the operation's finally block.
      this.nextFocus = row?.querySelector(wasQuantity ? '[data-cart-quantity]' : '[data-cart-remove]') || this.querySelector('[data-cart-close]');
    }
    const count = Number(content.dataset.cartCount);
    document.querySelectorAll('.site-header__cart').forEach((link) => {
      let badge = link.querySelector('.site-header__cart-count');
      if (!badge && count > 0) {
        badge = document.createElement('span');
        badge.className = 'site-header__cart-count';
        badge.setAttribute('aria-live', 'polite');
        link.append(badge);
      }
      if (badge) { badge.textContent = count; badge.hidden = count === 0; }
    });
  }

  async read() {
    const url = new URL(this.root, location.origin);
    url.searchParams.set('section_id', this.dataset.sectionId);
    const response = await fetch(url, { cache: 'no-store', signal: AbortSignal.timeout(15000) });
    if (!response.ok) throw new Error(this.dataset.refreshErrorLabel);
    this.render(await response.text());
  }

  async refresh() {
    if (this.busy) return;
    this.setBusy(true);
    this.error.hidden = true;
    this.message(this.dataset.loadingLabel);
    try {
      await this.read();
      this.message('');
    } catch {
      this.showError(this.dataset.refreshErrorLabel, true);
    } finally { this.finish(); }
  }

  showError(message, hideContent = false) {
    this.message('');
    this.error.querySelector('[data-cart-error-message]').textContent = message;
    this.error.hidden = false;
    if (hideContent) this.querySelector('[data-cart-content]').hidden = true;
  }

  finish() {
    this.setBusy(false);
    if (this.nextFocus && this.dialog.open) this.nextFocus.focus({ preventScroll: true });
    this.nextFocus = null;
    this.focusKey = null;
  }

  async add(data, trigger) {
    if (this.busy) return false;
    this.open(trigger);
    return this.mutate('add', data);
  }

  async change(key, quantity) {
    if (this.busy || !Number.isInteger(quantity) || quantity < 0) return false;
    return this.mutate('change', { id: key, quantity });
  }

  async mutate(action, data) {
    this.focusKey = this.contains(document.activeElement) ? document.activeElement.closest('[data-cart-item]')?.dataset.cartKey : null;
    this.focusQuantity = document.activeElement.matches('[data-cart-quantity]');
    this.setBusy(true);
    this.error.hidden = true;
    this.message(this.dataset.loadingLabel);
    let confirmed = false;
    try {
      const headers = { Accept: 'application/json' };
      const sectionData = { sections: this.dataset.sectionId, sections_url: this.root };
      let body;
      if (data instanceof FormData) {
        data.set('sections', sectionData.sections);
        data.set('sections_url', sectionData.sections_url);
        body = data;
      } else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify({ ...data, ...sectionData });
      }
      const response = await fetch(`${this.root}cart/${action}.js`, { method: 'POST', headers, body, signal: AbortSignal.timeout(15000) });
      const result = await response.json();
      if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : this.dataset.errorLabel);
      confirmed = true;
      const html = result.sections?.[this.dataset.sectionId];
      if (html) this.render(html);
      else await this.read();
      this.message(action === 'add' ? this.dataset.addedLabel : this.dataset.updatedLabel, action === 'add');
      return true;
    } catch (error) {
      // An interrupted response may still have changed the cart. Refresh only; never repeat a POST.
      let refreshed = false;
      try { await this.read(); refreshed = true; } catch { /* Offer a read-only retry or the full cart. */ }
      if (confirmed && refreshed) {
        this.message(action === 'add' ? this.dataset.addedLabel : this.dataset.updatedLabel, action === 'add');
        return true;
      }
      const message = confirmed ? this.dataset.refreshErrorLabel : (error.message && error.name === 'Error' ? error.message : this.dataset.errorLabel);
      this.showError(message || this.dataset.errorLabel, !refreshed);
      return false;
    } finally { this.finish(); }
  }
}
if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
